import { APP_CONFIG_TAB_PATHS, appConfigTabUrl } from '../../resources/appConfig/tabs';

/**
 * Classe aplicada no item que o react-admin acendeu só porque a URL atual
 * COMEÇA com o `to` dele. O estilo que desfaz a marcação está no tema
 * (`src/theme/index.js`), porque é lá que ela é pintada.
 */
export const MENU_ITEM_SUPPRESSED_CLASS = 'MenuItemLink-suppressed';

/**
 * Rotas que moram DEBAIXO de outro item do menu na URL, mas são item próprio.
 *
 * O `<MenuItemLink>` do react-admin decide o estado ativo com
 * `useMatch({ path: to, end: false })` — e esse `end` é fixo dentro do
 * componente, não dá para passar por prop. Resultado: em
 * `/chains/chainId/whatsapp-numbers/<id>` acendiam DOIS itens, "Rede"
 * (`/chains`) junto com "Números de WhatsApp".
 *
 * `/chains/chainId` resolve os 25+ recursos aninhados na rede de uma vez: o
 * `chainId` é segmento LITERAL no nome do recurso (ver `App.tsx`), então ele é
 * a fronteira exata entre "página da rede" e "página de outro cadastro". Assim
 * `/chains/<uuid>` (editar a rede) continua acendendo "Rede", como deve.
 *
 * As abas da tela Aplicativo entram pelo mesmo motivo: a Home é `/app-config` e
 * acenderia junto com cada uma das outras.
 */
const NESTED_ROUTE_PREFIXES = [
  '/chains/chainId',
  '/attendant-goal-boards/monitor',
  ...Object.values(APP_CONFIG_TAB_PATHS).filter(Boolean).map(appConfigTabUrl),
];

const toSegments = (path: string) => path.split('/').filter(Boolean);

/** `segments` está dentro de `prefix` (ou é igual a ele), comparando segmento a segmento. */
const isUnder = (segments: string[], prefix: string[]) =>
  segments.length >= prefix.length && prefix.every((segment, i) => segments[i] === segment);

const isStrictAncestor = (ancestor: string[], descendant: string[]) =>
  ancestor.length < descendant.length && isUnder(descendant, ancestor);

/** A URL atual pertence a um item mais específico do que `to`. */
export const isSuppressedRoute = (pathname: string, to: string) => {
  const pathnameSegments = toSegments(pathname);
  const toSegs = toSegments(to);

  return NESTED_ROUTE_PREFIXES.some(prefix => {
    const prefixSegments = toSegments(prefix);
    return isUnder(pathnameSegments, prefixSegments) && isStrictAncestor(toSegs, prefixSegments);
  });
};

/**
 * O item com este `to` acende nesta URL?
 *
 * Mesma regra do react-admin — `useMatch` com `end: false`, ou seja, prefixo
 * segmento a segmento — menos as rotas suprimidas acima. Quem usa isto é o
 * grupo do menu, para saber se a navegação está dentro dele; o item em si
 * continua sendo aceso pelo próprio react-admin.
 */
export const matchesMenuRoute = (pathname: string, to: string) =>
  isUnder(toSegments(pathname), toSegments(to)) && !isSuppressedRoute(pathname, to);
